import { memo } from "react"

import { SessionActionsMenu } from "@/components/layout/Common/SessionActionsMenu"
import { SwimEmptyState } from "@/components/molecules/SwimEmptyState"
import type { SessionResponse } from "@/types/session"
import { Flex, Table } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface SessionListProps {
  sessions: SessionResponse[]
}

const SessionList = memo(({ sessions }: SessionListProps) => {
  if (!sessions?.length) {
    return <SwimEmptyState resource="sessions" actionTo="/sessions" />
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>Name</Table.ColumnHeader>
          <Table.ColumnHeader>Slug</Table.ColumnHeader>
          <Table.ColumnHeader>Description</Table.ColumnHeader>
          <Table.ColumnHeader>Session Type</Table.ColumnHeader>
          <Table.ColumnHeader>Start Date</Table.ColumnHeader>
          <Table.ColumnHeader>End Date</Table.ColumnHeader>
          <Table.ColumnHeader>Registration Opens</Table.ColumnHeader>
          <Table.ColumnHeader>Registration Closes</Table.ColumnHeader>
          <Table.ColumnHeader>Early Bird Deadline</Table.ColumnHeader>
          <Table.ColumnHeader>Makeup Classes Allowed</Table.ColumnHeader>
          <Table.ColumnHeader>No Class Dates</Table.ColumnHeader>
          <Table.ColumnHeader>Is Active</Table.ColumnHeader>
          <Table.ColumnHeader>Is Published</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sessions.map((session) => (
          <Table.Row key={session.id}>
            <Table.Cell truncate maxW="sm">
              {session.id}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(session.name, "str")}</Table.Cell>
            <Table.Cell>{formatFieldValue(session.slug, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.description, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.session_type, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.start_week, "date")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.end_week, "date")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.registration_opens, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.registration_closes, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.early_bird_deadline, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.makeup_classes_allowed, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.no_class_dates, "dict")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.is_active, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.is_published, "bool")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(session.notes, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(session.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <SessionActionsMenu session={session} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

SessionList.displayName = "SessionList"

export default SessionList
